import React from 'react';

import { Avatar, Box, IconButton, Link, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { KeyboardArrowDownOutlined } from '@mui/icons-material';
import { useTabsSyncedCallbackTrigger } from '@uplift/hooks';
import { LogOutIcon } from '@uplift/icons';

import { ClosableMenu } from '../../../../lib/simple-menu/closable-menu.component';
import { AutoLogoutTrigger } from '../../../../molecules';
import { NavigationExternalLink } from '../NavigationExternalLink';
import { NavigationProfileMenuItem } from './NavigationProfileMenuItem.component';
import { useTimezone } from '@uplift/contexts';
import { CalendarDateService } from '@uplift/services';

const useStyles = makeStyles()({
  avatar: {
    width: '28px',
    height: '28px',
  },
  menuList: {
    marginTop: '3px'
  },
  name: {
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'none'
    }
  }
});


export type NavigationProfileMenuProps = {
  avatarSrc?: string;
  showLinks?: boolean;
  showAvatar?: boolean;
  showTerms?: boolean;
  topNavButton?: React.ReactNode;
  adminName?: string;
};

export const NavigationProfileMenu: React.FC<NavigationProfileMenuProps> = ({ showAvatar = true, showTerms = true, adminName = false, topNavButton, ...props }) => {
  const timezone = useTimezone();
  const currentYear = CalendarDateService.date().tz(timezone).year();
  const { classes } = useStyles();
  const [ anchorEl, setAnchorEl ] = React.useState<HTMLElement>();
  const logoutTrigger = useTabsSyncedCallbackTrigger(AutoLogoutTrigger.logoutManual);

  const handleClose = () => {
    setAnchorEl(undefined);
  };

  return (
    <Box display="flex" alignItems="center">
      {showAvatar && (
        <IconButton size="small" onClick={e => setAnchorEl(e.currentTarget.parentElement as HTMLElement)}>
          <Avatar className={classes.avatar} src={props.avatarSrc} alt="" />
        </IconButton>
      )
      }
      {topNavButton && (
        <Box pr="40px" alignItems="center">
          {topNavButton}
        </Box>
      )}
      {adminName && (
        <Link onClick={e => setAnchorEl(e.currentTarget.parentElement as HTMLElement)} className={classes.name}>
          <Box mr={2} display="flex" gap="8px">
            <Typography variant="body1" color="textPrimary" data-testid="nav-profile-admin-name">{adminName}</Typography>
            <KeyboardArrowDownOutlined style={{ color: '#757575' }} data-testid="nav-profile-arrow-down-icon"/>
          </Box>
        </Link>
      )}
      <ClosableMenu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        PopoverClasses={{ root: classes.menuList }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        data-testid="nav-profile-menu"
      >
        {props.children}
        <NavigationProfileMenuItem label="Log Out" icon={LogOutIcon} onClick={() => logoutTrigger()} />
        {showTerms && (
          <Box px={2} pt={1}>
            <Typography variant="body2" color="textSecondary" style={{ fontSize: '12px' }}>
              <NavigationExternalLink href="https://www.joinuplift.co/tos" data-testid="nav-profile-terms">Terms</NavigationExternalLink>
              · <NavigationExternalLink href="https://www.joinuplift.co/privacy" data-testid="nav-profile-privacy">Privacy</NavigationExternalLink>
              · UpLift © {currentYear}
            </Typography>
          </Box>
        )}
      </ClosableMenu>
    </Box>
  );
};
